import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Skeleton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  CalendarX,
  Clock3,
  Edit3,
  Eye,
  MessageSquare,
  Plus,
  User,
  X,
} from 'lucide-react';
import { broadcastApi } from '../../../api';
import useConfirm from '../../../hooks/useConfirm';
import { useUIStore } from '../../../store';

const BroadcastSchedulePage = ({
  scheduledBroadcasts = [],
  sess = { role: 'user', uid: '', mobile: '' },
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { confirm } = useConfirm();
  const [broadcasts, setBroadcasts] = useState(scheduledBroadcasts);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ type: '', message: '' });
  const showSnackbar = useUIStore((state) => state.showSnackbar);
  const isAdmin = sess.role === 'admin' || sess.role === 'superadmin';
  const cardSx = {
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
    boxShadow: 'none',
  };
  const primaryButtonSx = {
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: `0 10px 20px ${alpha(theme.palette.primary.main, 0.2)}`,
  };

  useEffect(() => {
    setBroadcasts(scheduledBroadcasts);
  }, [scheduledBroadcasts]);

  useEffect(() => {
    loadSchedules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status.type !== 'success' || !status.message) return;
    showSnackbar({ message: status.message, severity: 'success' });
    setStatus({ type: '', message: '' });
  }, [showSnackbar, status]);

  const loadSchedules = async () => {
    try {
      setLoading(true);
      const response = await broadcastApi.getScheduleHistory();
      const list = response?.data?.data || response?.data || [];
      setBroadcasts(list);
      setStatus({ type: '', message: '' });
    } catch (error) {
      const message = error?.response?.data?.message || 'Failed to load scheduled broadcasts';
      setStatus({ type: 'error', message });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date
      .toLocaleString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'Asia/Kolkata',
      })
      .replace(/,/g, '');
  };

  const handleCancelSchedule = async (broadcastId) => {
    const isConfirmed = await confirm({
      title: 'Cancel Scheduled Broadcast',
      message: 'Are you sure you want to cancel this scheduled broadcast?',
      confirmText: 'Cancel Broadcast',
      confirmColor: 'error',
      severity: 'warning',
    });
    if (!isConfirmed) return;

    try {
      await broadcastApi.cancelSchedule({ campaignId: broadcastId });
      setStatus({ type: 'success', message: 'Scheduled broadcast cancelled successfully' });
      setBroadcasts((prev) => prev.filter((b) => (b.id || b.broadcastId) !== broadcastId));
    } catch (error) {
      console.error('Error cancelling schedule:', error);
      const message = error?.response?.data?.message || 'Failed to cancel scheduled broadcast';
      setStatus({ type: 'error', message });
    }
  };

  const handleReschedule = (broadcast) => {
    navigate(`/broadcast/create?reschedule=${broadcast.id}`);
  };

  const handleViewDetails = (broadcast) => {
    navigate(`/broadcast/detail/${broadcast.id}`);
  };

  const getStatusChip = (status) => {
    const chips = {
      pending: { color: 'warning', label: 'Pending' },
      scheduled: { color: 'info', label: 'Scheduled' },
      processing: { color: 'primary', label: 'Processing' },
      sent: { color: 'success', label: 'Sent' },
      failed: { color: 'error', label: 'Failed' },
      cancelled: { color: 'default', label: 'Cancelled' },
    };
    const chip = chips[status] || chips.pending;
    return <Chip label={chip.label} color={chip.color} size="small" variant="outlined" />;
  };

  const stats = [
    { label: 'Scheduled', color: 'primary', value: broadcasts.filter((b) => b.status === 'scheduled').length },
    { label: 'Processing', color: 'info', value: broadcasts.filter((b) => b.status === 'processing').length },
    { label: 'Completed', color: 'success', value: broadcasts.filter((b) => b.status === 'sent').length },
    {
      label: 'Failed/Cancelled',
      color: 'error',
      value: broadcasts.filter((b) => b.status === 'failed' || b.status === 'cancelled').length,
    },
  ];

  return (
    <Box sx={{ p: 2, backgroundColor: alpha(theme.palette.primary.main, 0.02), minHeight: '100%' }}>
      <Card sx={{ ...cardSx, mb: 2, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)` }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography variant="h5" fontWeight={700}>Scheduled Broadcasts</Typography>
              <Typography variant="body2" color="text.secondary">
                Plan, monitor, and manage future sends
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Plus size={16} />}
              onClick={() => navigate('/broadcast/create')}
              sx={primaryButtonSx}
            >
              Schedule New Broadcast
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {status.message && status.type === 'error' && (
        <Typography
          color="error"
          variant="body2"
          sx={{ mb: 2 }}
        >
          {status.message}
        </Typography>
      )}

      {loading ? (
        <Stack spacing={2}>
          {Array.from({ length: 3 }).map((_, index) => (
            <Card key={`schedule-skeleton-${index}`} variant="outlined">
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Stack spacing={1}>
                      <Skeleton variant="text" width="60%" height={28} />
                      <Skeleton variant="text" width="70%" />
                      <Skeleton variant="text" width="50%" />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Stack spacing={1}>
                      <Skeleton variant="rounded" width={90} height={24} />
                      <Skeleton variant="text" width="60%" />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                      <Skeleton variant="rounded" width={70} height={32} />
                      <Skeleton variant="rounded" width={90} height={32} />
                      <Skeleton variant="rounded" width={80} height={32} />
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}

          <Grid container spacing={2}>
            {Array.from({ length: 4 }).map((_, idx) => (
              <Grid item xs={12} sm={6} md={3} key={`schedule-stat-${idx}`}>
                <Card>
                  <CardContent>
                    <Stack alignItems="center" spacing={0.5}>
                      <Skeleton variant="text" width={60} height={32} />
                      <Skeleton variant="text" width={90} />
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Stack>
      ) : (
        <Stack spacing={2}>
          {broadcasts && broadcasts.length > 0 ? (
            broadcasts.map((broadcast) => (
              <Card key={broadcast.id || broadcast.broadcastId} variant="outlined" sx={cardSx}>
                <CardContent>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                      <Stack spacing={0.5}>
                        <Typography variant="h6">
                          {broadcast.name || `Broadcast ${broadcast.id || broadcast.broadcastId}`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <Clock3 size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                          Scheduled: {formatDate(broadcast.scheduledTime || broadcast.scheduledAt)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <User size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                          Recipients: {broadcast.stats?.totalRecipients ||
                            broadcast.stats?.total_recipients ||
                            broadcast.stats?.recipients ||
                            broadcast.stats?.total ||
                            broadcast.recipientCount ||
                            broadcast.recipients_count ||
                            broadcast.total_recipients ||
                            (Array.isArray(broadcast.recipients) ? broadcast.recipients.length : 0)}
                        </Typography>
                        {broadcast.message && (
                          <Typography variant="body2" color="text.secondary">
                            <MessageSquare size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                            {broadcast.message.substring(0, 100)}
                            {broadcast.message.length > 100 ? '...' : ''}
                          </Typography>
                        )}
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Stack spacing={0.5}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body2" color="text.secondary">
                            Status:
                          </Typography>
                          {getStatusChip(broadcast.status || 'scheduled')}
                        </Stack>
                        {broadcast.template && (
                          <Typography variant="caption" color="text.secondary">
                            Template: {broadcast.template}
                          </Typography>
                        )}
                        {isAdmin && broadcast.createdBy && (
                          <Typography variant="caption" color="text.secondary">
                            Created by: {broadcast.createdBy}
                          </Typography>
                        )}
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Stack direction="row" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} spacing={1} flexWrap="wrap">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Eye size={16} />}
                          onClick={() => handleViewDetails(broadcast)}
                        >
                          View
                        </Button>
                        {(broadcast.status === 'scheduled' || broadcast.status === 'pending') && (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              startIcon={<Edit3 size={16} />}
                              onClick={() => handleReschedule(broadcast)}
                            >
                              Reschedule
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<X size={16} />}
                              onClick={() => handleCancelSchedule(broadcast.id || broadcast.broadcastId)}
                            >
                              Cancel
                            </Button>
                          </>
                        )}
                      </Stack>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card sx={cardSx}>
              <CardContent>
                <Stack spacing={1} alignItems="center" textAlign="center" py={4}>
                  <CalendarX size={48} />
                  <Typography variant="h6">No Scheduled Broadcasts</Typography>
                  <Typography color="text.secondary">
                    You don&apos;t have any scheduled broadcasts at the moment.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Plus size={16} />}
                    onClick={() => navigate('/broadcast/create')}
                  >
                    Schedule Your First Broadcast
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          )}

          {broadcasts && broadcasts.length > 0 && (
            <Grid container spacing={2}>
              {stats.map((item) => (
                <Grid item xs={12} sm={6} md={3} key={item.label}>
                  <Card sx={cardSx}>
                    <CardContent>
                      <Stack alignItems="center" spacing={0.5}>
                        <Typography variant="h4" color={item.color}>
                          {item.value}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.label}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default BroadcastSchedulePage;
